import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { AvailabilityRuleDetailListItem } from '@app/vinter/models/availability-rule/availability-rule-detail-list-item';
import { AvailabilityRuleListItem } from '@app/vinter/models/availability-rule/availability-rule-list-item';
import { EntityStateEnum } from '@app/shared/common/enums';

interface DayDef {
  dayOfWeek: number;
  label: string;
  fullName: string;
}

@Component({
  selector: 'app-availability-weekly-hours',
  templateUrl: './availability-weekly-hours.component.html',
  styleUrls: ['./availability-weekly-hours.component.scss']
})
export class AvailabilityWeeklyHoursComponent {
  @Input() availabilityRule: AvailabilityRuleListItem | null = null;
  @Input() timezone: string = 'Eastern Time - US & Canada';
  @Input() readonly: boolean = false;

  @Output() ruleChange = new EventEmitter<AvailabilityRuleListItem>();
  @Output() timezoneClick = new EventEmitter<void>();
  @Output() hasOverlapChange = new EventEmitter<boolean>();

  activeDropdownKey: string | null = null;
  slotLimitWarningDay: number | null = null;
  readonly MAX_SLOTS = 3;
  private limitWarningTimer: any = null;

  readonly dayDefs: DayDef[] = [
    { dayOfWeek: 7, label: 'S', fullName: 'Pazar' },
    { dayOfWeek: 1, label: 'M', fullName: 'Pazartesi' },
    { dayOfWeek: 2, label: 'T', fullName: 'Salı' },
    { dayOfWeek: 3, label: 'W', fullName: 'Çarşamba' },
    { dayOfWeek: 4, label: 'T', fullName: 'Perşembe' },
    { dayOfWeek: 5, label: 'F', fullName: 'Cuma' },
    { dayOfWeek: 6, label: 'S', fullName: 'Cumartesi' },
  ];

  readonly timeOptions: string[] = [
    '00:00', '00:30', '01:00', '01:30', '02:00', '02:30',
    '03:00', '03:30', '04:00', '04:30', '05:00', '05:30',
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
    '21:00', '21:30', '22:00', '22:30', '23:00', '23:30',
  ];

  // --- Slot queries ---

  getActiveSlots(dayOfWeek: number): AvailabilityRuleDetailListItem[] {
    if (!this.availabilityRule?.availabilityRuleDetails) return [];
    return this.availabilityRule.availabilityRuleDetails
      .filter(d =>
        d.dayOfWeek === dayOfWeek &&
        d.isActive &&
        d.entityState !== EntityStateEnum.Deleted
      )
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  isUnavailable(dayOfWeek: number): boolean {
    return this.getActiveSlots(dayOfWeek).length === 0;
  }

  // --- endTime options: only times strictly after startTime ---

  endTimeOptions(slot: AvailabilityRuleDetailListItem): string[] {
    const start = this.normalizeTime(slot.startTime);
    return this.timeOptions.filter(t => t > start);
  }

  // --- Overlap detection ---

  private toMinutes(time: string): number {
    const [h, m] = this.normalizeTime(time).split(':').map(Number);
    return h * 60 + m;
  }

  hasOverlap(slot: AvailabilityRuleDetailListItem): boolean {
    const slots = this.getActiveSlots(slot.dayOfWeek);
    const start = this.toMinutes(slot.startTime);
    const end = this.toMinutes(slot.endTime);
    return slots.some(other => {
      if (other === slot) return false;
      const otherStart = this.toMinutes(other.startTime);
      const otherEnd = this.toMinutes(other.endTime);
      return start < otherEnd && end > otherStart;
    });
  }

  get hasAnyOverlap(): boolean {
    if (!this.availabilityRule?.availabilityRuleDetails) return false;
    return this.dayDefs.some(day =>
      this.getActiveSlots(day.dayOfWeek).some(slot => this.hasOverlap(slot))
    );
  }

  // --- Add / Remove ---

  addSlot(dayOfWeek: number): void {
    if (!this.availabilityRule) return;
    if (this.getActiveSlots(dayOfWeek).length >= this.MAX_SLOTS) {
      this.slotLimitWarningDay = dayOfWeek;
      clearTimeout(this.limitWarningTimer);
      this.limitWarningTimer = setTimeout(() => {
        this.slotLimitWarningDay = null;
      }, 2000);
      return;
    }
    this.slotLimitWarningDay = null;
    const activeSlots = this.getActiveSlots(dayOfWeek);
    let newStart: string;
    let newEnd: string;
    if (activeSlots.length === 0) {
      newStart = '09:00';
      newEnd = '17:00';
    } else {
      const lastEndTime = this.normalizeTime(activeSlots[activeSlots.length - 1].endTime);
      newStart = this.addOneHour(lastEndTime);
      newEnd = this.addOneHour(newStart);
    }
    const slot: AvailabilityRuleDetailListItem = {
      id: crypto.randomUUID(),
      startTime: newStart,
      endTime: newEnd,
      dayOfWeek,
      isActive: true,
      availabilityRuleID: this.availabilityRule.id,
      entityState: EntityStateEnum.Added,
      xmin: '0',
    };
    this.availabilityRule.availabilityRuleDetails = [
      ...this.availabilityRule.availabilityRuleDetails,
      slot,
    ];
    this.emitChange();
  }

  removeSlot(slot: AvailabilityRuleDetailListItem): void {
    const details = this.availabilityRule!.availabilityRuleDetails;
    if (slot.entityState === EntityStateEnum.Added) {
      const idx = details.indexOf(slot);
      if (idx > -1) details.splice(idx, 1);
    } else {
      slot.entityState = EntityStateEnum.Deleted;
    }
    this.activeDropdownKey = null;
    if (this.slotLimitWarningDay === slot.dayOfWeek) {
      this.slotLimitWarningDay = null;
    }
    this.emitChange();
  }

  setTime(slot: AvailabilityRuleDetailListItem, type: 'start' | 'end', value: string): void {
    if (type === 'start') {
      slot.startTime = value;
      // reset endTime if it's no longer valid
      if (this.normalizeTime(slot.endTime) <= value) {
        slot.endTime = this.addOneHour(value);
      }
    } else {
      slot.endTime = value;
    }
    if (slot.entityState !== EntityStateEnum.Added) {
      slot.entityState = EntityStateEnum.Modified;
    }
    this.activeDropdownKey = null;
    this.emitChange();
  }

  // --- Helpers ---

  slotKey(slot: AvailabilityRuleDetailListItem): string {
    if (slot.id) return slot.id;
    const idx = this.availabilityRule!.availabilityRuleDetails.indexOf(slot);
    return `new-${slot.dayOfWeek}-${idx}`;
  }

  addOneHour(time: string): string {
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m + 60;
    const clampedMinutes = Math.min(totalMinutes, 23 * 60 + 30);
    const newH = Math.floor(clampedMinutes / 60);
    const newM = clampedMinutes % 60;
    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  }

  normalizeTime(time: string): string {
    if (!time) return '';
    return time.length > 5 ? time.substring(0, 5) : time;
  }

  toggleDropdown(event: MouseEvent, key: string): void {
    event.stopPropagation();
    this.activeDropdownKey = this.activeDropdownKey === key ? null : key;
  }

  isOpen(key: string): boolean {
    return this.activeDropdownKey === key;
  }

  onTimezoneClick(): void {
    this.timezoneClick.emit();
  }

  private emitChange(): void {
    this.ruleChange.emit({ ...this.availabilityRule! });
    this.hasOverlapChange.emit(this.hasAnyOverlap);
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.activeDropdownKey = null;
  }
}
